import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Suspense, lazy, useState, type ComponentType } from "react";
import {
  BarChart3,
  Briefcase,
  Building2,
  CalendarDays,
  ClipboardCheck,
  LayoutGrid,
  Layers,
  MapPin,
  Settings2,
  Tags,
  Menu,
  UtensilsCrossed,
  ArrowLeft,
  ExternalLink,
  Users,
  X,
} from "lucide-react";

import { useAuth } from "@/hooks/use-auth";
import { BS } from "@/components/admin/_shared";
import { WorkspaceHeader, WorkspacePageTitle } from "@/components/WorkspaceHeader";

const BusinessAnalyticsTab = lazy(() =>
  import("@/components/admin/business/BusinessAnalyticsTab").then((m) => ({ default: m.BusinessAnalyticsTab })),
);
const BusinessHomepageTab = lazy(() =>
  import("@/components/admin/business/BusinessHomepageTab").then((m) => ({ default: m.BusinessHomepageTab })),
);
const BusinessListingsTab = lazy(() =>
  import("@/components/admin/business/BusinessListingsTab").then((m) => ({ default: m.BusinessListingsTab })),
);
const PublishQueuePage = lazy(() =>
  import("@/components/admin/PublishQueuePage").then((m) => ({ default: m.PublishQueuePage })),
);
const BusinessServicesTab = lazy(() =>
  import("@/components/admin/business/BusinessServicesTab").then((m) => ({ default: m.BusinessServicesTab })),
);
const BusinessEventsTab = lazy(() =>
  import("@/components/admin/business/BusinessEventsTab").then((m) => ({ default: m.BusinessEventsTab })),
);
const BusinessLanesTab = lazy(() =>
  import("@/components/admin/business/BusinessLanesTab").then((m) => ({ default: m.BusinessLanesTab })),
);
const BusinessTaxonomyTab = lazy(() =>
  import("@/components/admin/business/BusinessTaxonomyTab").then((m) => ({ default: m.BusinessTaxonomyTab })),
);
const BusinessSettingsTab = lazy(() =>
  import("@/components/admin/business/BusinessSettingsTab").then((m) => ({ default: m.BusinessSettingsTab })),
);

const TrainFactoryMapTab = lazy(() =>
  import("@/components/admin/business/TrainFactoryMapTab").then((m) => ({ default: m.TrainFactoryMapTab })),
);
const BusinessMenusTab = lazy(() =>
  import("@/components/admin/business/BusinessMenusTab").then((m) => ({ default: m.BusinessMenusTab })),
);

type TabKey =
  | "homepage"
  | "train-factory"
  | "listings"
  | "lanes"
  | "review"
  | "services"
  | "events"
  | "menus"
  | "taxonomy"
  | "analytics"
  | "settings";

const TAB_KEYS = new Set<string>([
  "homepage",
  "train-factory",
  "listings",
  "lanes",
  "review",
  "services",
  "events",
  "menus",
  "taxonomy",
  "analytics",
  "settings",
]);

const TABS: {
  key: TabKey;
  group: "Home" | "Catalog" | "Site" | "System";
  label: string;
  navLabel: string;
  icon: ComponentType<{ className?: string; strokeWidth?: number }>;
  description: string;
  Panel: ComponentType;
}[] = [
  {
    key: "analytics",
    group: "Home",
    label: "Dashboard",
    navLabel: "Dashboard",
    icon: BarChart3,
    description: "Views, clicks and enquiries at a glance.",
    Panel: BusinessAnalyticsTab,
  },
  {
    key: "listings",
    group: "Catalog",
    label: "Listings",
    navLabel: "Listings",
    icon: Building2,
    description: "Business listings and their metadata.",
    Panel: BusinessListingsTab,
  },
  {
    key: "services",
    group: "Catalog",
    label: "Services",
    navLabel: "Services",
    icon: Briefcase,
    description: "B2B offerings — meeting rooms, day passes and more.",
    Panel: BusinessServicesTab,
  },
  {
    key: "events",
    group: "Catalog",
    label: "Events",
    navLabel: "Events",
    icon: CalendarDays,
    description: "Corporate and networking events.",
    Panel: BusinessEventsTab,
  },
  {
    key: "menus",
    group: "Catalog",
    label: "Menus",
    navLabel: "Menus",
    icon: UtensilsCrossed,
    description: "Restaurant menus, PDFs and opening hours.",
    Panel: BusinessMenusTab,
  },
  {
    key: "lanes",
    group: "Catalog",
    label: "Lanes",
    navLabel: "Lanes",
    icon: Layers,
    description: "Pick which places show in each Business lane.",
    Panel: BusinessLanesTab,
  },
  {
    key: "train-factory",
    group: "Site",
    label: "Train Factory",
    navLabel: "Train Factory",
    icon: MapPin,
    description: "Place numbered markers on the illustrated Konepaja map.",
    Panel: TrainFactoryMapTab,
  },
  {
    key: "review",
    group: "Catalog",
    label: "Review queue",
    navLabel: "Queue",
    icon: ClipboardCheck,
    description: "Approve or send back listings submitted for publishing.",
    Panel: PublishQueuePage,
  },
  {
    key: "homepage",
    group: "Site",
    label: "Homepage",
    navLabel: "Home",
    icon: LayoutGrid,
    description: "Hero slides and homepage sections for Business.",
    Panel: BusinessHomepageTab,
  },
  {
    key: "taxonomy",
    group: "Site",
    label: "Categories",
    navLabel: "Categories",
    icon: Tags,
    description: "Business categories and attributes.",
    Panel: BusinessTaxonomyTab,
  },
  {
    key: "settings",
    group: "System",
    label: "Settings",
    navLabel: "Settings",
    icon: Settings2,
    description: "Business mode and search filters.",
    Panel: BusinessSettingsTab,
  },
];

const GROUP_ORDER = ["Home", "Catalog", "Site", "System"] as const;

const BOTTOM_TABS = [
  { key: "analytics" as TabKey, label: "Dash", icon: BarChart3 },
  { key: "listings" as TabKey, label: "Listings", icon: Building2 },
  { key: "events" as TabKey, label: "Events", icon: CalendarDays },
  { key: "homepage" as TabKey, label: "Home", icon: LayoutGrid },
] as const;

export const Route = createFileRoute("/_authenticated/business-admin")({
  validateSearch: (search: Record<string, unknown>): { tab: TabKey; place?: string } => {
    const tab =
      typeof search.tab === "string" && TAB_KEYS.has(search.tab) ? (search.tab as TabKey) : ("analytics" as TabKey);
    const place = typeof search.place === "string" && search.place.trim() ? search.place.trim() : undefined;
    return place ? { tab, place } : { tab };
  },
  head: () => ({
    meta: [
      { title: "Business admin — What" },
      { name: "description", content: "Separate admin workspace for the What Business platform." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: BusinessAdminRoute,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-rose-600">Failed to load: {(error as Error).message}</div>
  ),
});

function TabFallback() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl border bg-white animate-pulse" style={{ borderColor: "#eceff3" }} />
        ))}
      </div>
      <div className="h-72 rounded-xl border bg-white animate-pulse" style={{ borderColor: "#eceff3" }} />
    </div>
  );
}

function BusinessAdminRoute() {
  const { tab, place } = Route.useSearch();
  const navigate = useNavigate();
  const { isAdmin, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const active = TABS.find((t) => t.key === tab) ?? TABS[0];
  const Panel = active.Panel;

  const go = (key: TabKey) => {
    setSidebarOpen(false);
    navigate({ to: "/business-admin", search: { tab: key } });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: "#f4f6f8", fontFamily: "'Poppins', system-ui, sans-serif" }}>
        <div className="h-[53px] flex-shrink-0" style={{ background: "#212529" }} />
        <div className="flex-1 flex">
          <div className="hidden md:block w-[232px] border-r bg-[#f8f9fb]" style={{ borderColor: "#eceff3" }} />
          <div className="flex-1 p-7">
            <TabFallback />
          </div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#f4f6f8" }}>
        <div className="bg-white border rounded-xl shadow-sm p-8 max-w-md text-center" style={{ borderColor: "#eceff3" }}>
          <h1 className="text-lg font-semibold text-[#212529]">Business admin</h1>
          <p className="mt-2 text-sm text-[#6c757d]">You don't have access to this workspace.</p>
          <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: BS.primary }}>
            <ArrowLeft className="size-4" /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh overflow-hidden flex flex-col" style={{ background: "#f4f6f8", fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <WorkspaceHeader
        current="business"
        onMenu={() => setSidebarOpen((v) => !v)}
        end={
          <a
            href="/business"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 h-9 rounded-full px-2.5 text-white/70 hover:text-white hover:bg-white/10 text-[12px] sm:text-[13px] font-medium"
          >
            <ExternalLink className="size-4 opacity-80" />
            <span className="hidden sm:inline">Site</span>
          </a>
        }
      />

      <div className="flex-1 flex relative min-h-0">
        {sidebarOpen ? (
          <button
            type="button"
            aria-label="Close navigation"
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        ) : null}

        <aside
          className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static top-[53px] md:top-auto bottom-0 left-0 z-40 w-[232px] min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] border-r bg-[#f8f9fb] transition-transform duration-150 ease-out shadow-xl md:shadow-none`}
          style={{ borderColor: "#eceff3" }}
        >
          <div className="mb-1 flex items-center justify-between px-3 pt-3 md:hidden">
            <span className="text-sm font-semibold text-[#212529]">Menu</span>
            <button type="button" onClick={() => setSidebarOpen(false)} aria-label="Close" className="rounded-lg p-1.5 hover:bg-[#eef1f5]">
              <X className="size-4" />
            </button>
          </div>
          <div className="px-2.5 py-3 pb-10">
            {GROUP_ORDER.map((group) => {
              const items = TABS.filter((t) => t.group === group);
              if (items.length === 0) return null;
              return (
                <div key={group} className="mb-3">
                  <div className="px-2.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b95a1]">
                    {group}
                  </div>
                  <nav className="space-y-0.5">
                    {items.map((t) => {
                      const Icon = t.icon;
                      const on = t.key === active.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => go(t.key)}
                          className="w-full flex items-center gap-2 px-1.5 py-[6px] rounded-lg text-[13px] font-medium text-left transition-colors duration-150"
                          style={{
                            background: on ? BS.primary : "transparent",
                            color: on ? "white" : "#1f2937",
                          }}
                          onMouseEnter={(e) => {
                            if (!on) e.currentTarget.style.background = "#eef1f5";
                          }}
                          onMouseLeave={(e) => {
                            if (!on) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <span
                            className="size-6 rounded-md flex items-center justify-center shrink-0"
                            style={{
                              background: on ? "rgba(255,255,255,0.18)" : "#eceff3",
                              color: on ? "white" : "#4b5563",
                            }}
                          >
                            <Icon className="size-3.5" strokeWidth={1.85} />
                          </span>
                          <span className="flex-1 truncate leading-none">{t.navLabel}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              );
            })}
          </div>
        </aside>

        <main className="min-w-0 flex-1 min-h-0 p-3 pb-24 sm:p-5 md:p-7 md:pb-7 overflow-y-auto overflow-x-hidden">
          <div className="mx-auto w-full max-w-[1320px]">
            {!(tab === "menus" && place) ? (
              <WorkspacePageTitle workspace="Business" title={active.label} description={active.description} />
            ) : null}
            <Suspense fallback={<TabFallback />}>
              <Panel />
            </Suspense>
          </div>
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 border-t bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden"
        style={{ borderColor: "#eceff3" }}
        aria-label="Business admin sections"
      >
        <div className="grid grid-cols-5">
          {BOTTOM_TABS.map((t) => {
            const Icon = t.icon;
            const on = t.key === active.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => go(t.key)}
                className="flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors active:scale-95"
                style={{ color: on ? BS.primary : "#6c757d" }}
                aria-current={on ? "page" : undefined}
              >
                <Icon className="size-5" strokeWidth={1.85} />
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center gap-0.5 py-2 text-[10px] font-medium text-[#6c757d] transition-colors active:scale-95"
          >
            <Menu className="size-5" />
            <span>More</span>
          </button>
        </div>
      </nav>
    </div>
  );
}
